import styled, { css } from "styled-components";
import { BadgeWrapperProps } from "./types";
import { SPACING, ANIMATION } from "../../utils/constants";

// Size configurations
const sizeStyles = {
  small: css`
    padding: ${SPACING.xxs}px ${SPACING.sm}px;
    font-size: var(--text-xs);
    font-weight: 500;
    letter-spacing: 0.025em;

    .badge-icon {
      width: 12px;
      height: 12px;
      margin-right: ${SPACING.xs}px;
    }
  `,
  medium: css`
    padding: ${SPACING.xs}px ${SPACING.sm + SPACING.xs}px;
    font-size: var(--text-base);
    font-weight: 550;
    letter-spacing: 0.02em;

    .badge-icon {
      width: 14px;
      height: 14px;
      margin-right: ${SPACING.xs + SPACING.xxs}px;
    }
  `,
  large: css`
    padding: ${SPACING.xs + SPACING.xxs}px ${SPACING.md}px;
    font-size: var(--text-md);
    font-weight: 600;
    letter-spacing: 0.015em;

    .badge-icon {
      width: 16px;
      height: 16px;
      margin-right: ${SPACING.sm}px;
    }
  `,
};

// Variant configurations for solid style
const solidVariantStyles = {
  default: css`
    background: var(--color-gray-200);
    color: var(--color-text-default);
    border-color: var(--color-gray-300);
  `,
  success: css`
    background: var(--color-success);
    color: var(--color-white);
    border-color: var(--color-success);
  `,
  error: css`
    background: var(--color-error);
    color: var(--color-white);
    border-color: var(--color-error);
  `,
  warning: css`
    background: var(--color-warning);
    color: var(--color-black);
    border-color: var(--color-warning);
  `,
  info: css`
    background: var(--color-info);
    color: var(--color-white);
    border-color: var(--color-info);
  `,
  primary: css`
    background: var(--color-primary);
    color: var(--color-white);
    border-color: var(--color-primary);
  `,
  secondary: css`
    background: var(--color-secondary);
    color: var(--color-white);
    border-color: var(--color-secondary);
  `,
};

// Variant configurations for soft style (subtle backgrounds with new soft variants)
const softVariantStyles = {
  default: css`
    background: var(--color-gray-background);
    color: var(--color-text-default);
    border-color: var(--color-gray-border);
  `,
  success: css`
    background: var(--color-status-success-background-soft);
    color: var(--color-status-success);
    border-color: var(--color-status-success-border-soft);
  `,
  error: css`
    background: var(--color-status-error-background-soft);
    color: var(--color-status-error);
    border-color: var(--color-status-error-border-soft);
  `,
  warning: css`
    background: var(--color-status-warning-background-soft);
    color: var(--color-status-warning);
    border-color: var(--color-status-warning-border-soft);
  `,
  info: css`
    background: var(--color-status-info-background-soft);
    color: var(--color-status-info);
    border-color: var(--color-status-info-border-soft);
  `,
  primary: css`
    background: var(--color-tab-hover-background);
    color: var(--color-primary);
    border-color: var(--color-primary);
    opacity: 0.25;
  `,
  secondary: css`
    background: var(--color-hover-background);
    color: var(--color-secondary);
    border-color: var(--color-secondary);
    opacity: 0.25;
  `,
};

// Variant configurations for outlined style
const outlinedVariantStyles = {
  default: css`
    background: transparent;
    color: var(--color-text-default);
    border-color: var(--color-border);
  `,
  success: css`
    background: transparent;
    color: var(--color-status-success);
    border-color: var(--color-status-success);
  `,
  error: css`
    background: transparent;
    color: var(--color-status-error);
    border-color: var(--color-status-error);
  `,
  warning: css`
    background: transparent;
    color: var(--color-status-warning);
    border-color: var(--color-status-warning);
  `,
  info: css`
    background: transparent;
    color: var(--color-status-info);
    border-color: var(--color-status-info);
  `,
  primary: css`
    background: transparent;
    color: var(--color-primary);
    border-color: var(--color-primary);
  `,
  secondary: css`
    background: transparent;
    color: var(--color-secondary);
    border-color: var(--color-secondary);
  `,
};

// Helper function to get hover background color for outlined badges
const getOutlinedHoverBackground = (variant: BadgeWrapperProps["$variant"]) => {
  const variantMap = {
    default: "var(--color-hover-overlay)",
    success: "var(--color-status-success-background)",
    error: "var(--color-status-error-background)",
    warning: "var(--color-status-warning-background)",
    info: "var(--color-status-info-background)",
    primary: "var(--color-tab-hover-background)",
    secondary: "var(--color-hover-background)",
  };
  return variantMap[variant];
};

export const BadgeWrapper = styled.span<BadgeWrapperProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid;
  white-space: nowrap;
  transition: ${ANIMATION.transitions.all};
  line-height: 1;

  .badge-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  .badge-text {
    line-height: 1;
  }

  /* Apply size styles */
  ${({ $size }) => sizeStyles[$size]};

  /* Apply variant styles based on style prop */
  ${({ $variant, $style }) => {
    if ($style === "soft") return softVariantStyles[$variant];
    if ($style === "outlined") return outlinedVariantStyles[$variant];
    return solidVariantStyles[$variant];
  }};

  /* Hover effect for outlined style */
  ${({ $style, $variant }) =>
    $style === "outlined" &&
    css`
      &:hover {
        background: ${getOutlinedHoverBackground($variant)};
      }
    `};
`;
